import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { SUBSCRIPTION_LEVELS, SUBSCRIPTION_LEVEL_MAP } from "@/components/subscription/require-subscription";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useState, useEffect, useMemo } from "react";
import {
  getUserSubscriptionRecord,
  resolveEffectiveSubscriptionState,
} from "@/lib/subscription-consistency";
import { hasPaidSubscriptionAccess } from "@/lib/subscription-access";

export function useSubscription() {
  const { user } = useFirebaseAuth();
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [subscriptionRecord, setSubscriptionRecord] = useState<Record<string, unknown> | null>(null);
  const [subscriptionRecordLoading, setSubscriptionRecordLoading] = useState(true);

  // Fetch subscription plans data using direct Firebase onSnapshot (Fueld-company approach)
  useEffect(() => {
    if (!user?.id) {
      setSubscriptionPlans([]);
      setPlansLoading(false);
      return;
    }

    setPlansLoading(true);

    const plansRef = collection(db, 'subscriptionPlans');
    const q = query(plansRef, where('isActive', '==', true));
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const plans = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort by price in memory
        const sortedPlans = plans.sort((a: any, b: any) => {
          const getPrice = (plan: any) => {
            if (plan.tier === 'FREE' || plan.slug === 'free' || plan.name === 'Free') {
              return 0;
            }
            return Number(plan.price || 0);
          };

          const aPrice = getPrice(a);
          const bPrice = getPrice(b);
          return aPrice - bPrice;
        });

        setSubscriptionPlans(sortedPlans);
        setPlansLoading(false);
      },
      () => {
        // Silently handle errors to avoid console noise
        setSubscriptionPlans([]);
        setPlansLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setSubscriptionRecord(null);
      setSubscriptionRecordLoading(false);
      return;
    }

    let cancelled = false;
    setSubscriptionRecordLoading(true);

    void getUserSubscriptionRecord(user.id)
      .then((record) => {
        if (!cancelled) {
          setSubscriptionRecord(record);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSubscriptionRecord(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSubscriptionRecordLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Memoize subscription calculations
  const subscriptionCalculations = useMemo(() => {
    const effectiveSubscription = resolveEffectiveSubscriptionState(
      user,
      subscriptionRecord
    );
    const subscriptionTier = effectiveSubscription.tier;
    const userSubscriptionLevel = subscriptionTier
      ? SUBSCRIPTION_LEVEL_MAP[subscriptionTier] || SUBSCRIPTION_LEVELS.FREE
      : SUBSCRIPTION_LEVELS.FREE;

    const hasAccess = (requiredLevel: keyof typeof SUBSCRIPTION_LEVELS) => {
      return userSubscriptionLevel >= SUBSCRIPTION_LEVELS[requiredLevel];
    };

    const tierEntry = Object.entries(SUBSCRIPTION_LEVEL_MAP).find(
      ([, level]) => level === userSubscriptionLevel
    );
    const currentPlanName = tierEntry
      ? tierEntry[0].charAt(0).toUpperCase() + tierEntry[0].slice(1).toLowerCase()
      : "Free";

    const subscriptionStatus = effectiveSubscription.status;
    const subscriptionSubStatus =
      (subscriptionRecord as { subStatus?: unknown } | null)?.subStatus
      ?? subscriptionStatus;
    const subscriptionStartsAt =
      (subscriptionRecord as { currentPeriodStart?: unknown } | null)?.currentPeriodStart ?? null;
    const subscriptionExpiresAt =
      (subscriptionRecord as { currentPeriodEnd?: unknown } | null)?.currentPeriodEnd ?? null;
    const cancelAtPeriodEnd =
      Boolean((subscriptionRecord as { cancelAtPeriodEnd?: unknown } | null)?.cancelAtPeriodEnd);
    const stripeCustomerId =
      (subscriptionRecord as { stripeCustomerId?: unknown } | null)?.stripeCustomerId ?? null;

    const currentPlan = subscriptionPlans.find(plan =>
      plan.tier?.toUpperCase() === subscriptionTier?.toUpperCase() ||
      plan.slug?.toLowerCase() === subscriptionTier?.toLowerCase()
    );
    const hasPaidAccess = hasPaidSubscriptionAccess({
      tier: subscriptionTier,
      status: effectiveSubscription.status,
      cancelAtPeriodEnd,
      currentPeriodEnd: subscriptionExpiresAt,
    });

    return {
      userSubscriptionLevel,
      planName: currentPlanName,
      hasAccess,
      isNobility: hasAccess("NOBILITY"),
      isRoyalty: hasAccess("ROYALTY"),
      isImperial: hasAccess("IMPERIAL"),
      subscriptionStatus,
      subscriptionSubStatus,
      subscriptionStartsAt,
      subscriptionExpiresAt,
      cancelAtPeriodEnd,
      stripeCustomerId,
      subscriptionPlans,
      currentPlan,
      subscriptionTier,
      subscriptionSource: effectiveSubscription.source,
      isSubscriptionConsistent: effectiveSubscription.isConsistent,
      hasPaidAccess,
      loading: plansLoading || subscriptionRecordLoading,
    };
  }, [user, subscriptionRecord, subscriptionPlans, plansLoading, subscriptionRecordLoading]);

  return subscriptionCalculations;
}
